import type { CSSProperties } from 'react';

import type { Participant } from '../fixtures/domain.js';
import { Avatar } from './Avatar.js';
import styles from './AvatarStack.module.css';

interface AvatarStackProps {
  /** Only what a face needs, so a caller holding something narrower than a
   *  Participant (a Chat Day's guest, say) can draw its people here too. */
  people: Pick<Participant, 'id' | 'name' | 'avatarUrl' | 'locked'>[];
  /** How many faces to show before the rest collapse into a "+N". */
  max: number;
  size: number;
  /**
   * What the "+N" opens.
   *
   * Optional only because some callers are already inside a button — an event
   * card is one big press target, and a nested `<button>` is invalid markup the
   * browser resolves by dropping one of them. Those callers pass nothing and
   * get a plain "+N", with the list living wherever their own press leads. A
   * caller that *can* offer a trip should still pass one: a chip naming people
   * it will not show, in a place that opens nothing, is a dead end.
   */
  onMore?: () => void;
}

/**
 * Who a thing is about, as faces. Past `max` the rest become a "+N" that hands
 * the "show everyone" decision to the caller (a hover popover taller than the
 * dialog it sits in just gets clipped).
 */
export function AvatarStack({ people, max, size, onMore }: AvatarStackProps) {
  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const rest = people.length - max;
  // The overlap and ring scale with the face, or a flat seam reads as one avatar
  // eating the next one's initials at larger sizes.
  const overlap = Math.round(size * 0.22);
  const ring = size >= 40 ? 3 : 2;

  return (
    <span
      className={styles.stack}
      style={{ '--stack-overlap': `-${overlap}px`, '--stack-ring': `${ring}px` } as CSSProperties}
    >
      {shown.map((person) => (
        <span key={person.id} className={styles.slot} title={person.name}>
          <Avatar name={person.name} src={person.avatarUrl} locked={person.locked} size={size} />
        </span>
      ))}

      {rest > 0 && (
        <span className={styles.slot}>
          {onMore ? (
            <button
              type="button"
              className={styles.more}
              style={{ width: size, height: size }}
              onClick={onMore}
              aria-label={`Show all ${people.length} recipients`}
            >
              +{rest}
            </button>
          ) : (
            // Not a disabled button: a control that cannot be pressed still
            // takes focus in some browsers and announces itself as a control.
            // This is a count, so it is drawn as one.
            <span className={styles.more} style={{ width: size, height: size }} aria-hidden="true">
              +{rest}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
